import { createIcons, icons } from "lucide";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { SplitText } from "gsap/SplitText";
import Swiper from "swiper";
import lightGallery from "lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, SplitText, DrawSVGPlugin);

/**
 * Base class for all components
 */
class Component {
	constructor(selector) {
		this.element =
			typeof selector === "string"
				? document.querySelector(selector)
				: selector;
		this.isInitialized = false;
	}

	init() {
		if (!this.element || this.isInitialized) return;
		this.isInitialized = true;
		this.setup();
	}

	setup() {
		// Override in subclasses
	}

	destroy() {
		this.isInitialized = false;
	}
}

/**
 * Smooth scroll handler
 */
class SmoothScroll extends Component {
	constructor() {
		super(document.body);
		this.lenis = null;
	}

	setup() {
		this.lenis = new Lenis({ autoRaf: false });
		this.startRAF();
	}

	startRAF() {
		const raf = (time) => {
			this.lenis.raf(time);
			ScrollTrigger.update();
			requestAnimationFrame(raf);
		};
		requestAnimationFrame(raf);
	}

	destroy() {
		if (this.lenis) {
			this.lenis.destroy();
		}
		super.destroy();
	}
}

/**
 * Navigation animation handler
 */
class Navigation extends Component {
	constructor(selector = "nav") {
		super(selector);
		this.logoSelector = "nav .custom-logo";
	}

	setup() {
		this.animateNavigation();
		this.animateLogo();
	}

	animateNavigation() {
		gsap.set(this.element, { backgroundColor: "rgba(86, 59, 19, 0.3)" });
		gsap.to(this.element, {
			scrollTrigger: {
				trigger: this.element,
				start: "top top",
				end: "+=170",
				scrub: true,
			},
			height: "80px",
			backgroundColor: "rgba(86, 59, 19, 0.7)",
		});
	}

	animateLogo() {
		const logo = document.querySelector(this.logoSelector);
		if (!logo) return;

		gsap.to(logo, {
			scrollTrigger: {
				trigger: this.element,
				start: "top top",
				end: "+=170",
				scrub: true,
			},
			scale: 0.8,
			transformOrigin: "left center",
		});
	}
}

/**
 * Text animation handler
 */
class TextAnimations extends Component {
	constructor() {
		super(document.body);
		this.animations = [];
		this.resizeTimeout = null;
		this.handleResize = this.handleResize.bind(this);
	}

	setup() {
		this.animateHeadings();
		this.animateSubheadings();
		this.animateParagraphs();
		this.addResizeListener();
	}

	addResizeListener() {
		window.addEventListener("resize", this.handleResize);
	}

	handleResize() {
		// Debounce resize events to avoid excessive calls
		clearTimeout(this.resizeTimeout);
		this.resizeTimeout = setTimeout(() => {
			this.refreshSplitText();
		}, 250);
	}

	refreshSplitText() {
		// Store current scroll position
		const currentScroll = window.pageYOffset;

		// Revert all existing SplitText instances
		this.animations.forEach((split) => {
			if (split && split.revert) {
				split.revert();
			}
		});
		this.animations = [];

		// Kill all existing ScrollTrigger instances for text animations
		ScrollTrigger.getAll().forEach((trigger) => {
			if (
				trigger.vars &&
				((trigger.vars.trigger &&
					typeof trigger.vars.trigger.matches === "function" &&
					trigger.vars.trigger.matches("h1, h2, h3, p")) ||
					(trigger.trigger &&
						typeof trigger.trigger.matches === "function" &&
						trigger.trigger.matches("h1, h2, h3, p")))
			) {
				trigger.kill();
			}
		});

		// Re-initialize animations
		this.animateHeadings();
		this.animateSubheadings();
		this.animateParagraphs();

		// Refresh ScrollTrigger
		ScrollTrigger.refresh();

		// Restore scroll position
		window.scrollTo(0, currentScroll);
	}

	animateHeadings() {
		document.querySelectorAll("h1, h3").forEach((el) => {
			const split = new SplitText(el, { type: "words, chars" });
			this.animations.push(split);

			gsap.from(split.chars, {
				duration: 1.1,
				y: 40,
				rotationX: 30,
				autoAlpha: 0,
				filter: "blur(4px)",
				ease: "power3.out",
				stagger: {
					each: 0.02,
					from: "start",
				},
				transformOrigin: "center bottom",
				scrollTrigger: {
					trigger: el,
					start: "top 80%",
					toggleActions: "play none none none",
				},
			});
		});
	}

	animateSubheadings() {
		document.querySelectorAll("h2").forEach((el) => {
			const split = new SplitText(el, { type: "lines" });
			this.animations.push(split);

			gsap.from(split.lines, {
				y: 5,
				autoAlpha: 0,
				ease: "power3.out",
				duration: 0.8,
				stagger: 0.15,
				scrollTrigger: {
					trigger: el,
					start: "top 80%",
					toggleActions: "play none none none",
				},
			});
		});
	}

	animateParagraphs() {
		document.querySelectorAll("p").forEach((el) => {
			const split = new SplitText(el, { type: "lines" });
			this.animations.push(split);

			gsap.from(split.lines, {
				y: 20,
				autoAlpha: 0,
				ease: "power3.out",
				duration: 1,
				stagger: 0.1,
				scrollTrigger: {
					trigger: el,
					start: "top 85%",
					toggleActions: "play none none none",
				},
			});
		});
	}

	destroy() {
		// Remove resize event listener
		window.removeEventListener("resize", this.handleResize);

		// Clear resize timeout
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}

		// Revert all SplitText instances
		this.animations.forEach((split) => split.revert());
		this.animations = [];
		super.destroy();
	}
}

/**
 * Parallax effects handler
 */
class ParallaxEffects extends Component {
	constructor() {
		super(document.body);
	}

	setup() {
		this.setupImageParallax();
		this.setupBackgroundParallax();
		this.setupContentParallax();
	}

	setupImageParallax() {
		document.querySelectorAll(".parallax-image").forEach((img) => {
			gsap.set(img, { scale: 1.1 });

			gsap.to(img, {
				scale: 1,
				ease: "none",
				scrollTrigger: {
					trigger: img.closest(".parallax-wrapper"),
					start: "top bottom",
					end: "bottom top",
					scrub: true,
				},
			});
		});
	}

	setupBackgroundParallax() {
		const bg = document.querySelector(".parallax-bg");
		if (!bg) return;

		gsap.to(bg, {
			yPercent: 50,
			ease: "none",
			scrollTrigger: {
				trigger: bg,
				start: "top top",
				end: "bottom top",
				scrub: true,
			},
		});

		gsap.to(bg, {
			scale: 1.1,
			ease: "none",
			scrollTrigger: {
				trigger: bg,
				start: "top bottom",
				end: "bottom top",
				scrub: true,
			},
		});
	}

	setupContentParallax() {
		const content = document.querySelector(".parallax-content");
		if (!content) return;

		gsap.to(content, {
			yPercent: -20,
			opacity: 0,
			ease: "power.inOut",
			scrollTrigger: {
				trigger: ".parallax-bg",
				start: "top top",
				end: "bottom top",
				scrub: true,
			},
		});
	}
}

/**
 * SVG animations handler
 */
class SVGAnimations extends Component {
	constructor() {
		super(document.body);
	}

	setup() {
		document.querySelectorAll(".animate-svg-lines").forEach((svg) => {
			gsap.from(svg.querySelectorAll("path"), {
				drawSVG: 0,
				duration: 2,
				ease: "power2.out",
				stagger: 0.2,
				scrollTrigger: {
					trigger: svg,
					start: "top 80%",
					toggleActions: "play none none none",
					once: true,
				},
			});
		});
	}
}

/**
 * Gallery slider component
 */
class GallerySlider extends Component {
	constructor(selector = ".gallery-slider") {
		super(selector);
		this.swiper = null;
	}

	setup() {
		if (!this.element) return;

		this.swiper = new Swiper(".gallery-slider .swiper", {
			slidesPerView: "auto",
			spaceBetween: 16,
			grabCursor: true,
			allowTouchMove: true,
		});
	}

	destroy() {
		this.swiper?.destroy();
		super.destroy();
	}
}

/**
 * Light gallery component
 */
class LightGallery extends Component {
	constructor(selector = "#lightgallery") {
		super(selector);
		this.gallery = null;
	}

	setup() {
		if (!this.element) return;

		this.gallery = lightGallery(this.element, {
			thumbnail: true,
			animateThumb: false,
			showThumbByDefault: false,
			plugins: [lgThumbnail],
		});
	}

	destroy() {
		this.gallery?.destroy();
		super.destroy();
	}
}

/**
 * Accordion component for expandable content sections
 */
class Accordion extends Component {
	constructor(selector = "[data-accordion]") {
		super(selector);
		this.accordionItems = [];
		this.activeIndex = null;
	}

	setup() {
		if (!this.element) return;

		this.accordionItems = Array.from(
			this.element.querySelectorAll("[data-accordion-item]"),
		);
		this.bindEvents();
	}

	bindEvents() {
		this.accordionItems.forEach((item, index) => {
			const trigger = item.querySelector("[data-accordion-trigger]");
			const content = item.querySelector("[data-accordion-content]");

			if (trigger && content) {
				// Set initial state
				if (index === 0) {
					this.openItem(index, false); // Open first item by default without animation
					this.activeIndex = 0; // Set the active index
				} else {
					this.closeItem(index, false);
				}

				trigger.addEventListener("click", () => {
					if (this.activeIndex === index) {
						this.closeItem(index);
						this.activeIndex = null;
					} else {
						if (this.activeIndex !== null) {
							this.closeItem(this.activeIndex);
						}
						this.openItem(index);
						this.activeIndex = index;
					}
				});
			}
		});
	}

	openItem(index, animate = true) {
		const item = this.accordionItems[index];
		if (!item) return;

		const trigger = item.querySelector("[data-accordion-trigger]");
		const content = item.querySelector("[data-accordion-content]");
		const icon = trigger?.querySelector("[data-lucide]");

		if (animate) {
			gsap.to(content, {
				height: "auto",
				opacity: 1,
				duration: 0.5,
				ease: "power2.out",
				onComplete: () => {
					// Refresh ScrollTrigger after animation completes so text animations can trigger
					ScrollTrigger.refresh();
				},
			});
		} else {
			gsap.set(content, { height: "auto", opacity: 1 });
			// Refresh ScrollTrigger immediately for non-animated opens
			ScrollTrigger.refresh();
		}

		// Rotate icon to minus
		if (icon) {
			// icon.setAttribute("data-lucide", "minus");
			gsap.to(icon, {
				rotate: 135,
				ease: "power2.out",
				duration: 0.5,
			});

			// createIcons({ icons });
		}

		item.classList.add("active");
		icon.classList.remove("text-gold-400");
		icon.classList.add("text-green-200");
		trigger?.setAttribute("aria-expanded", "true");
	}

	closeItem(index, animate = true) {
		const item = this.accordionItems[index];
		if (!item) return;

		const trigger = item.querySelector("[data-accordion-trigger]");
		const content = item.querySelector("[data-accordion-content]");
		const icon = trigger?.querySelector("[data-lucide]");

		if (animate) {
			gsap.to(content, {
				height: 0,
				opacity: 0,
				duration: 0.5,
				ease: "power2.out",
				onComplete: () => {
					// Refresh ScrollTrigger after animation completes so text animations can trigger
					ScrollTrigger.refresh();
				},
			});
		} else {
			gsap.set(content, { height: 0, opacity: 0 });
			ScrollTrigger.refresh();
		}

		// Rotate icon to plus
		if (icon) {
			gsap.to(icon, {
				rotate: 0,
				ease: "power2.out",
				duration: 0.5,
			});
			// icon.setAttribute("data-lucide", "plus");
			// createIcons({ icons });
		}

		item.classList.remove("active");
		icon.classList.add("text-gold-400");
		icon.classList.remove("text-green-200");
		trigger?.setAttribute("aria-expanded", "false");
	}

	destroy() {
		this.accordionItems = [];
		this.activeIndex = null;
		super.destroy();
	}
}

/**
 * Animated cursor component
 */
class AnimatedCursor extends Component {
	constructor(selector = "#cursor") {
		super(selector);
		this.mouseX = 0;
		this.mouseY = 0;
		this.cursorX = 0;
		this.cursorY = 0;
		this.animationId = null;
		this.hoverElements = [];
		this.textElement = null;
	}

	setup() {
		if (!this.element) return;

		this.textElement = this.element.querySelector(".cursor-text");
		this.bindEvents();
		this.setupHoverElements();
		this.startAnimation();
	}

	bindEvents() {
		this.mouseMoveHandler = (e) => {
			this.mouseX = e.clientX;
			this.mouseY = e.clientY;
		};

		this.mouseLeaveHandler = () => {
			this.element.style.opacity = "0";
		};

		this.mouseEnterHandler = () => {
			this.element.style.opacity = "1";
		};

		document.addEventListener("mousemove", this.mouseMoveHandler);
		document.addEventListener("mouseleave", this.mouseLeaveHandler);
		document.addEventListener("mouseenter", this.mouseEnterHandler);
	}

	setupHoverElements() {
		this.hoverElements = document.querySelectorAll('[data-hover="true"]');

		this.hoverElements.forEach((element) => {
			const mouseEnterHandler = () => {
				this.element.classList.add("hover");
				const text = element.getAttribute("data-cursor-text");
				if (text && this.textElement) {
					this.textElement.textContent = text;
				}
			};

			const mouseLeaveHandler = () => {
				this.element.classList.remove("hover");
				if (this.textElement) {
					this.textElement.textContent = "";
				}
			};

			element.addEventListener("mouseenter", mouseEnterHandler);
			element.addEventListener("mouseleave", mouseLeaveHandler);

			// Store handlers for cleanup
			element._cursorHandlers = {
				mouseenter: mouseEnterHandler,
				mouseleave: mouseLeaveHandler,
			};
		});
	}

	startAnimation() {
		const animate = () => {
			if (this.isDestroyed) return;

			const diffX = this.mouseX - this.cursorX;
			const diffY = this.mouseY - this.cursorY;

			this.cursorX += diffX * 0.1;
			this.cursorY += diffY * 0.1;

			this.element.style.left = this.cursorX + "px";
			this.element.style.top = this.cursorY + "px";

			this.animationId = requestAnimationFrame(animate);
		};

		animate();
	}

	destroy() {
		// Cancel animation
		if (this.animationId) {
			cancelAnimationFrame(this.animationId);
		}

		// Remove event listeners
		document.removeEventListener("mousemove", this.mouseMoveHandler);
		document.removeEventListener("mouseleave", this.mouseLeaveHandler);
		document.removeEventListener("mouseenter", this.mouseEnterHandler);

		// Clean up hover elements
		this.hoverElements.forEach((element) => {
			if (element._cursorHandlers) {
				element.removeEventListener(
					"mouseenter",
					element._cursorHandlers.mouseenter,
				);
				element.removeEventListener(
					"mouseleave",
					element._cursorHandlers.mouseleave,
				);
				delete element._cursorHandlers;
			}
		});

		super.destroy();
	}
}

/**
 * Main application class
 */
class App {
	constructor() {
		this.components = new Map();
		this.isInitialized = false;
	}

	init() {
		if (this.isInitialized) return;

		// Initialize Lucide icons
		createIcons({ icons });

		// Initialize all components except text animations
		this.components.set("smoothScroll", new SmoothScroll());
		this.components.set("navigation", new Navigation());
		this.components.set("parallaxEffects", new ParallaxEffects());
		this.components.set("svgAnimations", new SVGAnimations());
		this.components.set("gallerySlider", new GallerySlider());
		this.components.set("lightGallery", new LightGallery());
		// this.components.set("animatedCursor", new AnimatedCursor());
		this.components.set("accordion", new Accordion());

		// Initialize all components except text animations
		this.components.forEach((component) => component.init());

		// Initialize text animations after fonts are loaded
		this.initTextAnimationsWhenFontsReady();

		this.isInitialized = true;
	}

	initTextAnimationsWhenFontsReady() {
		// Check if FontFace API is supported
		if ("fonts" in document) {
			document.fonts.ready.then(() => {
				this.components.set("textAnimations", new TextAnimations());
				this.components.get("textAnimations").init();
			});
		} else {
			// Fallback for browsers without FontFace API support
			// Wait a bit longer to ensure fonts are loaded
			setTimeout(() => {
				this.components.set("textAnimations", new TextAnimations());
				this.components.get("textAnimations").init();
			}, 500);
		}
	}

	getComponent(name) {
		return this.components.get(name);
	}

	destroy() {
		this.components.forEach((component) => component.destroy());
		this.components.clear();
		this.isInitialized = false;
	}
}

// Initialize the application
const app = new App();

class MobileMenu {
	constructor() {
		this.overlay = null;
		this.toggleBtn = null;
		this.menuItems = [];
		this.ctaBtn = null;
		this.servicesToggle = null;
		this.submenu = null;
		this.servicesIcon = null;
		this.hamburgerLines = [];

		this.isOpen = false;
		this.init();
	}

	init() {
		// Wait for DOM to be ready and get elements
		setTimeout(() => {
			this.overlay = document.getElementById("mobile-menu");
			this.toggleBtn = document.getElementById("mobile-menu-toggle");
			this.menuItems = document.querySelectorAll(".mobile-menu-item");
			this.ctaBtn = document.querySelector(".mobile-cta-btn");
			this.servicesToggle = document.querySelector(".mobile-services-toggle");
			this.submenu = document.getElementById("mobile-services-submenu");
			this.servicesIcon = document.querySelector(".services-icon");
			this.hamburgerLines = document.querySelectorAll(".hamburger-line");

			if (!this.overlay || !this.toggleBtn) {
				console.warn("Mobile menu elements not found");
				return;
			}

			this.setupInitialStates();
			this.bindEvents();
		}, 100);
	}

	setupInitialStates() {
		// Set initial states
		gsap.set(this.overlay, {
			opacity: 0,
			visibility: "hidden",
		});
		gsap.set(this.menuItems, {
			opacity: 0,
			y: 50,
		});
		if (this.ctaBtn) {
			gsap.set(this.ctaBtn, {
				opacity: 0,
				y: 30,
			});
		}
	}

	bindEvents() {
		// Single toggle button for open/close
		this.toggleBtn.addEventListener("click", () => {
			if (this.isOpen) {
				this.closeMenu();
			} else {
				this.openMenu();
			}
		});

		if (this.servicesToggle) {
			this.servicesToggle.addEventListener("click", () => this.toggleSubmenu());
		}

		// Close on outside click (only overlay area)
		this.overlay.addEventListener("click", (e) => {
			if (e.target === this.overlay) {
				this.closeMenu();
			}
		});

		// Close on escape key
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && this.isOpen) {
				this.closeMenu();
			}
		});
	}

	openMenu() {
		if (this.isOpen) return;
		this.isOpen = true;

		// Prevent body scroll
		document.body.style.overflow = "hidden";

		// Enable pointer events on overlay
		this.overlay.style.pointerEvents = "auto";

		// Animate hamburger to X
		gsap.to(this.hamburgerLines[0], {
			rotation: 45,
			y: 6,
			duration: 0.3,
			ease: "power2.inOut",
		});
		gsap.to(this.hamburgerLines[1], {
			opacity: 0,
			duration: 0.2,
		});
		gsap.to(this.hamburgerLines[2], {
			rotation: -45,
			y: -6,
			duration: 0.3,
			ease: "power2.inOut",
		});

		// Timeline for menu animation
		const tl = gsap.timeline();

		// Show overlay
		tl.to(this.overlay, {
			opacity: 1,
			visibility: "visible",
			duration: 0.4,
			ease: "power2.out",
		})

			// Animate menu items with stagger
			.to(
				this.menuItems,
				{
					opacity: 1,
					y: 0,
					duration: 0.6,
					stagger: 0.1,
					ease: "power3.out",
				},
				"-=0.2",
			);

		// Animate CTA button
		if (this.ctaBtn) {
			tl.to(
				this.ctaBtn,
				{
					opacity: 1,
					y: 0,
					duration: 0.5,
					ease: "power3.out",
				},
				"-=0.3",
			);
		}
	}

	closeMenu() {
		if (!this.isOpen) return;
		this.isOpen = false;

		// Timeline for closing animation
		const tl = gsap.timeline({
			onComplete: () => {
				document.body.style.overflow = "";
				// Disable pointer events on overlay
				this.overlay.style.pointerEvents = "none";
			},
		});

		// Create array of elements to animate
		const elementsToAnimate = [...this.menuItems];
		if (this.ctaBtn) {
			elementsToAnimate.push(this.ctaBtn);
		}

		// Animate out menu items first
		tl.to(elementsToAnimate, {
			opacity: 0,
			y: 50,
			duration: 0.4,
			stagger: 0.05,
			ease: "power2.in",
		})

			// Hide overlay AFTER menu items complete their animation
			.to(
				this.overlay,
				{
					opacity: 0,
					visibility: "hidden",
					duration: 0.25,
					ease: "power2.in",
				},
				"+=0.1", // Wait 0.1 seconds after menu items finish
			);

		// Reset hamburger (runs parallel to the timeline)
		gsap.to(this.hamburgerLines[0], {
			rotation: 0,
			y: 0,
			duration: 0.3,
			ease: "power2.inOut",
		});
		gsap.to(this.hamburgerLines[1], {
			opacity: 1,
			duration: 0.3,
			delay: 0.1,
		});
		gsap.to(this.hamburgerLines[2], {
			rotation: 0,
			y: 0,
			duration: 0.3,
			ease: "power2.inOut",
		});

		// Close submenu if open
		if (this.submenu && !this.submenu.classList.contains("max-h-0")) {
			this.closeSubmenu();
		}
	}

	toggleSubmenu() {
		const isOpen = this.servicesToggle.getAttribute("aria-expanded") === "true";

		if (isOpen) {
			this.closeSubmenu();
		} else {
			this.openSubmenu();
		}
	}

	openSubmenu() {
		this.servicesToggle.setAttribute("aria-expanded", "true");

		// First measure the natural height
		this.submenu.style.maxHeight = "none";
		this.submenu.style.opacity = "1";
		const naturalHeight = this.submenu.scrollHeight;
		this.submenu.style.maxHeight = "0";
		this.submenu.style.opacity = "0";

		// Create timeline for smoother animation
		const tl = gsap.timeline();

		// Set hardware acceleration for submenu items
		const submenuItems = this.submenu.querySelectorAll("li");
		gsap.set(submenuItems, { force3D: true });

		// Animate submenu container first
		tl.to(this.submenu, {
			opacity: 1,
			maxHeight: naturalHeight + "px",
			duration: 0.4,
			ease: "power2.out",
		})

			// Rotate plus to X (minus) - parallel with container
			.to(
				this.servicesIcon,
				{
					rotation: 45,
					duration: 0.3,
					ease: "power2.out",
				},
				0,
			)

			// Animate submenu items with hardware acceleration
			.fromTo(
				submenuItems,
				{
					opacity: 0,
					y: 8,
					force3D: true,
				},
				{
					opacity: 1,
					y: 0,
					duration: 0.25,
					stagger: 0.03,
					ease: "power2.out",
					force3D: true,
				},
				0.1,
			);
	}

	closeSubmenu() {
		this.servicesToggle.setAttribute("aria-expanded", "false");

		// Create timeline for smoother closing animation
		const tl = gsap.timeline();

		const submenuItems = this.submenu.querySelectorAll("li");

		// Animate submenu items out first
		tl.to(submenuItems, {
			opacity: 0,
			y: -3,
			duration: 0.15,
			stagger: 0.01,
			ease: "power2.in",
			force3D: true,
		})

			// Then close the container
			.to(
				this.submenu,
				{
					opacity: 0,
					maxHeight: 0,
					duration: 0.25,
					ease: "power2.in",
				},
				0.1,
			)

			// Rotate X back to plus - parallel with container
			.to(
				this.servicesIcon,
				{
					rotation: 0,
					duration: 0.3,
					ease: "power2.out",
				},
				0.1,
			);
	}
}

document.addEventListener("DOMContentLoaded", () => {
	app.init();
	new MobileMenu();
});

// Export for potential external use
export default app;
